<template>
  <article
    class="BenefitTile"
    :class="[`BenefitTile--${size}`]"
  >
    <header v-if="hasHeader" class="BenefitTile-header">
      <component
        :is="titleAs"
        v-if="title"
        class="BenefitTile-title"
        v-html="title"
      />

      <div v-if="hasStep" class="BenefitTile-step">
        <Icon
          v-if="icon"
          :icon="icon"
          size="lg"
          class="BenefitTile-stepIcon"
        />
        <span v-if="step !== undefined" class="BenefitTile-stepNumber">
          {{ step }}
        </span>
      </div>
    </header>

    <p v-if="body" class="BenefitTile-body" v-html="body" />
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Icon from '../../atoms/Icon/Icon.vue';
import type { BenefitTileProps } from './BenefitTile.model';

const props = withDefaults(defineProps<BenefitTileProps>(), {
  size: 'lg',
  titleAs: 'h3',
});

const hasStep = computed(() => Boolean(props.icon || props.step !== undefined));
const hasHeader = computed(() => Boolean(props.title || hasStep.value));
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text' as text;

@layer cvo-ui {
  $step-size: 44px;

  .BenefitTile {
    display: flex;
    flex-direction: column;
    min-width: 240px;
    border: vars.$molecules-tile-border-stroke solid vars.$molecules-tile-step-border-color;
    background-color: vars.$molecules-tile-step-bg-color;
    overflow: hidden;

    &--lg {
      padding: vars.$molecules-tile-lg-md-padding-top-bottom vars.$molecules-tile-lg-md-padding-left-right;
      border-radius: vars.$molecules-tile-lg-md-border-radius;
      gap: vars.$dimensions-d-4;
    }

    &--sm {
      padding: vars.$molecules-tile-sm-padding-top-bottom vars.$molecules-tile-sm-padding-left-right;
      border-radius: vars.$molecules-tile-sm-border-radius;
      gap: vars.$dimensions-d-4;
    }

    &-header {
      display: flex;
      flex-direction: row-reverse;
      align-items: center;
      justify-content: start;
      gap: vars.$dimensions-d-2;
      width: 100%;
    }

    &-title {
      flex: 1;
      margin: 0;
      color: vars.$molecules-tile-step-title-color;
      white-space: pre-wrap;

      .BenefitTile--lg & {
        @include text.heading('h5');
      }

      .BenefitTile--sm & {
        @include text.heading('h6');
      }
    }

    &-step {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: $step-size;
      min-height: $step-size;
      padding: vars.$dimensions-d-2;
      gap: vars.$dimensions-d-2;
      background-color: vars.$molecules-tile-step-icon-bg-color;
      border-radius: vars.$molecules-tile-step-border-radius;
      flex-shrink: 0;
    }

    &-stepIcon {
      color: vars.$molecules-tile-step-icon-color;
    }

    &-stepNumber {
      color: vars.$molecules-tile-step-icon-color;
      @include text.text('lg');
    }

    &-body {
      margin: 0;
      width: 100%;
      color: vars.$molecules-tile-step-text-color;

      .BenefitTile--lg & {
        @include text.text('md');
      }

      .BenefitTile--sm & {
        @include text.text('sm');
      }
    }
  }
}
</style>


